import React, { useId, useState } from 'react';
import { Plus } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type Incident,
  type IncidentFilter,
  type IncidentInput,
  type IncidentSeverity,
  type IncidentStatus,
  useDeleteIncidentMutation,
  useIncidentsQuery,
  useSaveIncidentMutation,
} from '@/client/api/incidents/incidents';
import { EmptyRow, PageShell, Th } from '../Scheduler/schedulerUi';

const SEVERITY: Record<IncidentSeverity, { label: string; cls: string }> = {
  sev1: { label: 'SEV1', cls: 'bg-red-50 text-red-700 border-red-200' },
  sev2: { label: 'SEV2', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  sev3: { label: 'SEV3', cls: 'bg-gray-50 text-gray-700 border-gray-200' },
};
const STATUSES: IncidentStatus[] = ['investigating', 'identified', 'monitoring', 'resolved'];
const FILTERS: { key: IncidentFilter; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];
const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const SELECT_CLASS = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** ISO UTC -> value for <input type="datetime-local"> in local time. */
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
/** datetime-local value (local time) -> ISO UTC. */
const toIso = (local: string) => new Date(local).toISOString();

const fmtLocal = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

function duration(i: Incident): string {
  const end = i.resolvedAt ? new Date(i.resolvedAt).getTime() : Date.now();
  const mins = Math.max(0, Math.round((end - new Date(i.startedAt).getTime()) / 60000));
  const text = mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
  return i.resolvedAt ? text : `ongoing (${text})`;
}

interface Draft {
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  area: string;
  startedAt: string;
  resolvedAt: string;
  impact: string;
  cause: string;
  fix: string;
  followUp: string;
  owner: string;
  link: string;
}

const toDraft = (i?: Incident): Draft => ({
  title: i?.title ?? '',
  severity: i?.severity ?? 'sev2',
  status: i?.status ?? 'investigating',
  area: i?.area ?? '',
  startedAt: toLocalInput(i?.startedAt ?? new Date().toISOString()),
  resolvedAt: toLocalInput(i?.resolvedAt ?? null),
  impact: i?.impact ?? '',
  cause: i?.cause ?? '',
  fix: i?.fix ?? '',
  followUp: i?.followUp ?? '',
  owner: i?.owner ?? '',
  link: i?.link ?? '',
});

const IncidentDialog: React.FC<{
  incident: Incident | null; // null = new
  canEdit: boolean;
  onClose: () => void;
}> = ({ incident, canEdit, onClose }) => {
  const formId = useId();
  const save = useSaveIncidentMutation();
  const del = useDeleteIncidentMutation();
  const [draft, setDraft] = useState<Draft>(() => toDraft(incident ?? undefined));
  const [error, setError] = useState('');
  const set =
    (k: keyof Draft) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setDraft((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) return setError('Title is required.');
    const resolved = draft.status === 'resolved';
    const body: IncidentInput = {
      ...(incident ? { id: incident.id } : {}),
      title: draft.title.trim(),
      severity: draft.severity,
      status: draft.status,
      area: draft.area.trim(),
      startedAt: draft.startedAt ? toIso(draft.startedAt) : undefined,
      // Resolved with no time given: stamp now. Reopened: clear it.
      resolvedAt: resolved ? (draft.resolvedAt ? toIso(draft.resolvedAt) : new Date().toISOString()) : null,
      impact: draft.impact,
      cause: draft.cause,
      fix: draft.fix,
      followUp: draft.followUp,
      owner: draft.owner.trim(),
      link: draft.link.trim(),
    };
    try {
      await save.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the incident.');
    }
  };

  const remove = async () => {
    if (!incident || !window.confirm('Delete this incident?')) return;
    try {
      await del.mutateAsync(incident.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the incident.');
    }
  };

  const text = (k: keyof Draft, label: string, extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="grid gap-1.5">
      <Label htmlFor={`${formId}-${k}`}>{label}</Label>
      <Input id={`${formId}-${k}`} value={draft[k]} onChange={set(k)} disabled={!canEdit} {...extra} />
    </div>
  );
  const area = (k: keyof Draft, label: string, placeholder: string) => (
    <div className="grid gap-1.5">
      <Label htmlFor={`${formId}-${k}`}>{label}</Label>
      <textarea
        id={`${formId}-${k}`}
        rows={2}
        value={draft[k]}
        onChange={set(k)}
        placeholder={placeholder}
        disabled={!canEdit}
        className={TEXTAREA_CLASS}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{incident ? 'Edit incident' : 'Log incident'}</DialogTitle>
          <DialogDescription>Times are shown in your local time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          {text('title', 'Title', { autoFocus: true, placeholder: 'What happened, in a line' })}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-severity`}>Severity</Label>
              <select id={`${formId}-severity`} className={SELECT_CLASS} value={draft.severity} onChange={set('severity')} disabled={!canEdit}>
                {(Object.keys(SEVERITY) as IncidentSeverity[]).map((s) => (
                  <option key={s} value={s}>{SEVERITY[s].label}</option>
                ))}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-status`}>Status</Label>
              <select id={`${formId}-status`} className={SELECT_CLASS} value={draft.status} onChange={set('status')} disabled={!canEdit}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{cap(s)}</option>
                ))}
              </select>
            </div>
            {text('area', 'Area', { placeholder: 'ff-api, ff-ui...' })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('startedAt', 'Started', { type: 'datetime-local' })}
            {text('resolvedAt', 'Resolved', { type: 'datetime-local', disabled: !canEdit || draft.status !== 'resolved' })}
          </div>
          {area('impact', 'Impact', 'Who or what was affected')}
          {area('cause', 'Cause', 'Root cause')}
          {area('fix', 'Fix', 'What resolved it')}
          {area('followUp', 'Follow-up', 'What we change so it does not recur')}
          <div className="grid gap-3 sm:grid-cols-2">
            {text('owner', 'Owner')}
            {text('link', 'Link', { placeholder: 'Issue, PR or runbook URL' })}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <DialogFooter className="gap-2 sm:justify-between">
            {incident && canEdit ? (
              <Button type="button" variant="outline" onClick={remove} disabled={del.isPending}>Delete</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              {canEdit && <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving...' : 'Save'}</Button>}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const IncidentsPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Incidents', href: '/admin/incidents' }]);
  const viewPerm = useHasPermission('system.config.view');
  const editPerm = useHasPermission('system.config.edit');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;

  const [filter, setFilter] = useState<IncidentFilter>('open');
  const [dialog, setDialog] = useState<{ incident: Incident | null } | null>(null);
  const q = useIncidentsQuery(filter);
  const save = useSaveIncidentMutation();
  const rows = q.data ?? [];

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Incidents" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Incidents" description="">
        <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
          <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Incidents</p>
          <p className="mt-1 text-sm text-gray-500">Ask an administrator for the system.config.view permission.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Incidents"
      description="Outages, bans and slowdowns: log them, track them to resolution, and keep a short postmortem."
      actions={
        canEdit && (
          <Button size="sm" onClick={() => setDialog({ incident: null })}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Log incident
          </Button>
        )
      }
    >
      <div className="flex gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`rounded-lg px-3 py-1.5 text-sm ${filter === f.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
              <Th>Severity</Th>
              <Th>Title</Th>
              <Th>Area</Th>
              <Th>Status</Th>
              <Th>Started</Th>
              <Th>Duration</Th>
              <Th>Owner</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {q.isError ? (
              <EmptyRow cols={8}>Could not load incidents.</EmptyRow>
            ) : rows.length === 0 ? (
              <EmptyRow cols={8} loading={q.isLoading}>
                {filter === 'open' ? 'No open incidents.' : filter === 'resolved' ? 'No resolved incidents.' : 'No incidents logged yet.'}
              </EmptyRow>
            ) : (
              rows.map((i) => (
                <tr key={i.id} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50" onClick={() => setDialog({ incident: i })}>
                  <td className="px-4 py-2">
                    <span className={`rounded border px-1.5 py-0.5 text-xs font-medium ${SEVERITY[i.severity]?.cls ?? ''}`}>
                      {SEVERITY[i.severity]?.label ?? i.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900">{i.title}</td>
                  <td className="px-4 py-2 text-gray-600">{i.area || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{cap(i.status)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">{fmtLocal(i.startedAt)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">{duration(i)}</td>
                  <td className="px-4 py-2 text-gray-600">{i.owner || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    {canEdit && i.status !== 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={save.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          save.mutate({ id: i.id, status: 'resolved', resolvedAt: new Date().toISOString() });
                        }}
                      >
                        Mark resolved
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {dialog && (
        <IncidentDialog
          key={dialog.incident?.id ?? 'new'}
          incident={dialog.incident}
          canEdit={canEdit}
          onClose={() => setDialog(null)}
        />
      )}
    </PageShell>
  );
};

export default IncidentsPage;
